import type { SummonDefinition, SummonSite } from '../../data/types';
import { nextU32, pick, seedRng } from '../../shared/prng';
import { clamp } from '../../shared/scalar';
import { isBarTick } from '../../shared/tempo';
import { IDLE_INPUT } from '../commands';
import type { ResolvedContent } from '../content';
import { lookup } from '../lookup';
import { spawnBossAt, edgePosition } from '../systems/spawning';
import { layDownTrap } from '../systems/loot';
import { handFree } from '../stats';
import { touchesObstacle } from '../obstacles';
import type {
  HeavyOfferingState,
  PlayerId,
  PlayerState,
  SimState,
  SummonState,
  Vec2,
} from '../state';
import type { StepContext } from '../systems/types';

export const HEAVY_HALF_LENGTH = 46;
const CARRY_SPEED = 2 / 3;

const posedBy = new WeakMap<SimState, { tick: number; players: Set<PlayerId> }>();

export function tookPoseKey(ctx: StepContext, playerId: PlayerId): boolean {
  const posed = posedBy.get(ctx.state);
  return posed?.tick === ctx.state.tick && posed.players.has(playerId);
}

export function summonOf(state: SimState): SummonState | undefined {
  return state.summon?.setId === state.setId ? state.summon : undefined;
}

export function burntTotal(summon: SummonState | undefined): number {
  return summon?.burnt.reduce((sum, burnt) => sum + burnt, 0) ?? 0;
}

function heavyEnd(heavy: HeavyOfferingState, end: 0 | 1): Vec2 {
  const side = end === 0 ? 1 : -1;
  return {
    x: heavy.x + side * heavy.ax * HEAVY_HALF_LENGTH,
    y: heavy.y + side * heavy.ay * HEAVY_HALF_LENGTH,
  };
}

export function summonActions(ctx: StepContext): void {
  const { state, content } = ctx;
  if (state.summon !== undefined && state.summon.setId !== state.setId) {
    leaveStage(state);
  }
  const definition = content.summons.get(state.setId);
  if (definition === undefined) {
    return;
  }
  for (const player of state.players) {
    if (player.downed || player.gone === true || player.dash !== undefined) {
      letGo(state, player);
    }
    if (player.downed || player.gone === true) {
      continue;
    }
    for (const action of ctx.commands.get(player.id)?.actions ?? []) {
      if (
        (action.type === 'placeTrap' || action.type === 'takeTrap') &&
        pose(ctx, definition, player)
      ) {
        markPosed(state, player.id);
      }
    }
  }
  const summon = summonOf(state);
  if (summon === undefined) {
    return;
  }
  for (const heavy of summon.heavy) {
    carry(ctx, definition, summon, heavy);
  }
  if (isBarTick(state.tick, ctx.tempo)) {
    burn(state, definition, summon);
  }
}

function markPosed(state: SimState, playerId: PlayerId): void {
  let posed = posedBy.get(state);
  if (posed?.tick !== state.tick) {
    posed = { tick: state.tick, players: new Set() };
    posedBy.set(state, posed);
  }
  posed.players.add(playerId);
}

function pose(ctx: StepContext, definition: SummonDefinition, player: PlayerState): boolean {
  const { state } = ctx;
  if (player.carrying !== undefined) {
    letGo(state, player);
    return true;
  }
  const summon = summonOf(state);
  if (player.offering !== undefined && summon !== undefined) {
    const source = player.offering;
    delete player.offering;
    const site = openSite(definition, summon, player);
    if (site === undefined) {
      summon.taken[source] = (summon.taken[source] ?? 1) - 1;
      return true;
    }
    summon.falling.push(site);
    state.events.push({
      type: 'offeringThrown',
      playerId: player.id,
      site,
      x: player.x,
      y: player.y,
    });
    return true;
  }
  const lying =
    freeEnd(summon, player, definition.reach) ?? halfCarriedAtPile(definition, summon, player);
  if (lying !== undefined) {
    grab(state, player, lying.heavy, lying.end);
    return true;
  }
  return take(ctx, definition, player);
}

function take(ctx: StepContext, definition: SummonDefinition, player: PlayerState): boolean {
  const { state, set } = ctx;
  const alone = state.players.length === 1;
  const taken = summonOf(state)?.taken;
  const source = definition.sources.findIndex(
    (source, index) =>
      (taken?.[index] ?? 0) < source.items && within(player, source, definition.reach),
  );
  const at = definition.sources[source];
  if (at === undefined) {
    return false;
  }
  if (alone && !handFree(player, set) && !layDownTrap(state, player)) {
    return false;
  }
  const summon = startSummon(state, definition);
  summon.taken[source] = (summon.taken[source] ?? 0) + 1;
  if (alone) {
    player.offering = source;
  } else {
    const away = unit(player.x - at.x, player.y - at.y) ?? { x: 1, y: 0 };
    const heavy: HeavyOfferingState = {
      id: state.nextEntityId,
      x: at.x,
      y: at.y,
      prevX: at.x,
      prevY: at.y,
      ax: away.x,
      ay: away.y,
      ends: [null, null],
    };
    state.nextEntityId += 1;
    summon.heavy.push(heavy);
    grab(state, player, heavy, 0);
  }
  const { x, y } = player;
  state.events.push({ type: 'offeringTaken', playerId: player.id, x, y, heavy: !alone });
  return true;
}

function startSummon(state: SimState, definition: SummonDefinition): SummonState {
  const current = summonOf(state);
  if (current !== undefined) {
    return current;
  }
  const summon: SummonState = {
    setId: state.setId,
    taken: definition.sources.map(() => 0),
    burnt: definition.sites.map(() => 0),
    order: [],
    falling: [],
    heavy: [],
    called: [],
  };
  state.summon = summon;
  return summon;
}

function grab(state: SimState, player: PlayerState, heavy: HeavyOfferingState, end: 0 | 1): void {
  heavy.ends[end] = player.id;
  player.carrying = { logId: heavy.id, end };
  pin(player, heavy, end);
  state.events.push({ type: 'heavyGrabbed', id: heavy.id, playerId: player.id });
}

function letGo(state: SimState, player: PlayerState): void {
  const carrying = player.carrying;
  if (carrying === undefined) {
    return;
  }
  delete player.carrying;
  const heavy = summonOf(state)?.heavy.find((each) => each.id === carrying.logId);
  if (heavy !== undefined) {
    heavy.ends[carrying.end] = null;
  }
  state.events.push({ type: 'heavyDropped', id: carrying.logId, playerId: player.id });
}

function leaveStage(state: SimState): void {
  for (const player of state.players) {
    delete player.offering;
    delete player.carrying;
  }
  delete state.summon;
}

const CALL_EVERY_BARS = 4;

export function summonBosses(ctx: StepContext): void {
  const { state, set, content, tempo } = ctx;
  const summon = summonOf(state);
  const definition = content.summons.get(state.setId);
  if (summon === undefined || definition === undefined) {
    delete state.bossesDue;
    return;
  }
  for (const event of state.events) {
    if (event.type === 'segment' && event.segment === 'drop' && burntTotal(summon) > 0) {
      state.bossesDue = burntTotal(summon);
      summon.called = [];
    }
  }
  const tier = set.tiers[state.set.tier];
  if (state.bossesDue === undefined || tier === undefined || !isBarTick(state.tick, tempo)) {
    return;
  }
  const bar = (state.tick - state.set.segmentStartTick) / tempo.ticksPerBar;
  if (state.set.segment !== 'drop' || bar !== CALL_EVERY_BARS * (summon.called.length + 1)) {
    return;
  }
  const kind = drawCalledBoss(state, content, summon.called);
  const boss = { ...lookup(content.enemies, kind, 'enemy'), ...tier.boss };
  const site = summon.order[summon.called.length] ?? 0;
  const gates = definition.sites[site]?.gates ?? [];
  const at =
    gates.length > 0
      ? inside(state, pick(state.rng, gates), boss.radius)
      : edgePosition(state.rng, state.arena, boss.radius);
  const enemy = spawnBossAt(ctx, boss, at);
  enemy.called = true;
  summon.called.push(kind);
  state.bossesDue -= 1;
  if (state.bossesDue <= 0) {
    delete state.bossesDue;
  }
  state.events.push({ type: 'bossCalled', id: enemy.id, kind, site, x: enemy.x, y: enemy.y });
}

const CALLED_STREAM = 0xca11;
// Stream values set aside for each drop's called bosses.
const CALLS_PER_DROP = 8;

// A stream of its own, so the bosses of the drops stay as they are.
function drawCalledBoss(
  state: SimState,
  content: ResolvedContent,
  called: readonly string[],
): string {
  const rng = seedRng((state.seed ^ CALLED_STREAM) >>> 0);
  const skip = (state.bosses?.length ?? 0) * CALLS_PER_DROP + called.length;
  for (let index = 0; index < skip; index++) {
    nextU32(rng);
  }
  const pool = [...content.enemies.values()]
    .filter((enemy) => enemy.behaviour === 'boss')
    .map((enemy) => enemy.id);
  const own = state.bosses?.at(-1);
  const fresh = pool.filter((id) => id !== own && !called.includes(id));
  return pick(rng, fresh.length > 0 ? fresh : pool);
}

function inside({ arena }: SimState, at: Vec2, radius: number): Vec2 {
  return {
    x: clamp(at.x, radius, arena.width - radius),
    y: clamp(at.y, radius, arena.height - radius),
  };
}

function carry(
  ctx: StepContext,
  definition: SummonDefinition,
  summon: SummonState,
  heavy: HeavyOfferingState,
): void {
  const { state, obstacles } = ctx;
  const [first, second] = heavy.ends.map((id) => playerOf(state, id));
  if (heavy.site === undefined && first !== undefined && second !== undefined) {
    const pace = Math.min(paceOf(first), paceOf(second)) * CARRY_SPEED;
    const a = moveOf(ctx, first);
    const b = moveOf(ctx, second);
    const from = heavyEnd(heavy, 0);
    const to = heavyEnd(heavy, 1);
    const endA = { x: from.x + a.x * pace, y: from.y + a.y * pace };
    const endB = { x: to.x + b.x * pace, y: to.y + b.y * pace };
    const axis = unit(endA.x - endB.x, endA.y - endB.y) ?? { x: heavy.ax, y: heavy.ay };
    const x = (endA.x + endB.x) / 2;
    const y = (endA.y + endB.y) / 2;
    const moved = { ...heavy, x, y, ax: axis.x, ay: axis.y };
    const blocked = ([first, second] as const).some((carrier, end) => {
      const at = { ...heavyEnd(moved, end === 0 ? 0 : 1), radius: carrier.radius };
      return !inArena(state, at) || touchesObstacle(at, obstacles);
    });
    if (!blocked) {
      heavy.x = x;
      heavy.y = y;
      heavy.ax = axis.x;
      heavy.ay = axis.y;
    }
  }
  if (first !== undefined) {
    pin(first, heavy, 0);
  }
  if (second !== undefined) {
    pin(second, heavy, 1);
  }
  if (heavy.site === undefined) {
    const site = definition.sites.findIndex(
      (site, index) => room(summon, site, index) > 0 && segmentReaches(heavy, site, site.radius),
    );
    if (site >= 0) {
      heavy.site = site;
    }
  }
}

function burn(state: SimState, definition: SummonDefinition, summon: SummonState): void {
  const sites = [...summon.falling];
  summon.falling.length = 0;
  summon.heavy = summon.heavy.filter((heavy) => {
    if (heavy.site === undefined) {
      return true;
    }
    for (const id of heavy.ends) {
      const player = playerOf(state, id);
      if (player !== undefined) {
        delete player.carrying;
      }
    }
    sites.push(heavy.site);
    return false;
  });
  for (const index of sites) {
    const site = definition.sites[index];
    if (site === undefined) {
      continue;
    }
    summon.burnt[index] = (summon.burnt[index] ?? 0) + 1;
    summon.order.push(index);
    state.events.push({
      type: 'offeringBurnt',
      look: definition.look,
      site: index,
      burnt: summon.burnt[index] ?? 0,
      total: burntTotal(summon),
      x: site.x,
      y: site.y,
    });
  }
}

function openSite(
  definition: SummonDefinition,
  summon: SummonState | undefined,
  at: Vec2,
): number | undefined {
  let best: number | undefined;
  let bestDistance = Infinity;
  definition.sites.forEach((site, index) => {
    const distance = squared(at, site);
    const reach = site.radius + definition.reach;
    if (distance <= reach * reach && distance < bestDistance && room(summon, site, index) > 0) {
      best = index;
      bestDistance = distance;
    }
  });
  return best;
}

function room(summon: SummonState | undefined, site: SummonSite, index: number): number {
  if (summon === undefined) {
    return site.capacity;
  }
  const coming =
    summon.falling.filter((each) => each === index).length +
    summon.heavy.filter((heavy) => heavy.site === index).length;
  return site.capacity - (summon.burnt[index] ?? 0) - coming;
}

function freeEnd(
  summon: SummonState | undefined,
  player: PlayerState,
  reach: number,
): { heavy: HeavyOfferingState; end: 0 | 1 } | undefined {
  for (const heavy of summon?.heavy ?? []) {
    if (heavy.site !== undefined) {
      continue;
    }
    for (const end of [0, 1] as const) {
      if (
        heavy.ends[end] === null &&
        within(player, { ...heavyEnd(heavy, end), radius: 0 }, reach)
      ) {
        return { heavy, end };
      }
    }
  }
  return undefined;
}

// One held end never moves a log, so a log taken at a pile lies on it until its second carrier comes.
function halfCarriedAtPile(
  definition: SummonDefinition,
  summon: SummonState | undefined,
  player: PlayerState,
): { heavy: HeavyOfferingState; end: 0 | 1 } | undefined {
  const piles = definition.sources.filter((source) => within(player, source, definition.reach));
  for (const heavy of summon?.heavy ?? []) {
    const [first, second] = heavy.ends;
    if (heavy.site !== undefined || (first === null) === (second === null)) {
      continue;
    }
    if (piles.some((pile) => within(heavy, pile, 0))) {
      return { heavy, end: first === null ? 0 : 1 };
    }
  }
  return undefined;
}

function pin(player: PlayerState, heavy: HeavyOfferingState, end: 0 | 1): void {
  const { x, y } = heavyEnd(heavy, end);
  player.x = x;
  player.y = y;
}

function playerOf(state: SimState, id: PlayerId | null): PlayerState | undefined {
  return id === null ? undefined : state.players.find((player) => player.id === id);
}

function paceOf(player: PlayerState): number {
  return player.speed * (player.slowFactor ?? 1);
}

function moveOf(ctx: StepContext, player: PlayerState): Vec2 {
  const move = (ctx.commands.get(player.id)?.input ?? IDLE_INPUT).move;
  const length = Math.sqrt(move.x * move.x + move.y * move.y);
  return length > 1 ? { x: move.x / length, y: move.y / length } : move;
}

function segmentReaches(heavy: HeavyOfferingState, at: Vec2, radius: number): boolean {
  const along = (at.x - heavy.x) * heavy.ax + (at.y - heavy.y) * heavy.ay;
  const t = clamp(along, -HEAVY_HALF_LENGTH, HEAVY_HALF_LENGTH);
  return squared(at, { x: heavy.x + heavy.ax * t, y: heavy.y + heavy.ay * t }) <= radius * radius;
}

function within(from: Vec2, at: Vec2 & { radius: number }, reach: number): boolean {
  const range = at.radius + reach;
  return squared(from, at) <= range * range;
}

function squared(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function unit(x: number, y: number): Vec2 | undefined {
  const length = Math.sqrt(x * x + y * y);
  return length > 1e-6 ? { x: x / length, y: y / length } : undefined;
}

function inArena({ arena }: SimState, at: Vec2 & { radius: number }): boolean {
  return (
    at.x >= at.radius &&
    at.y >= at.radius &&
    at.x <= arena.width - at.radius &&
    at.y <= arena.height - at.radius
  );
}
